import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { negociosApi } from "../services/apiClient";

const NegociosContext = createContext();

export const NegociosProvider = ({ children }) => {
    const { user, isLoading: authLoading, activeProfile, activeCompany } = useAuth();
    const [negocios, setNegocios] = useState([]);
    const [loadingNegocios, setLoadingNegocios] = useState(false);
    const [errorNegocios, setErrorNegocios] = useState(null);
    const isFetching = useRef(false);

    // Limpiar datos obsoletos ANTES de que cambie el perfil
    useEffect(() => {
        const handleProfileChanging = () => {
            setNegocios([]);
            setLoadingNegocios(true);
            isFetching.current = false;
        };
        window.addEventListener("crm:profileChanging", handleProfileChanging);
        return () => window.removeEventListener("crm:profileChanging", handleProfileChanging);
    }, []);

    const profileRef = useRef(activeProfile);
    const companyRef = useRef(activeCompany);
    useEffect(() => { profileRef.current = activeProfile; }, [activeProfile]);
    useEffect(() => { companyRef.current = activeCompany; }, [activeCompany]);

    // 1. Limpiar estado al cerrar sesión
    useEffect(() => {
        if (!authLoading && !user?.id) {
            setNegocios([]);
            setErrorNegocios(null);
        }
    }, [user?.id, authLoading]);

    /**
     * 2. Carga de datos unificada
     * Usa el contexto multiempresa (activeCompany) para filtrar negocios
     */
    const fetchNegocios = useCallback(async (prospecto_id = null, filters = {}) => {
        const p = profileRef.current;
        const c = companyRef.current;
        if (!user?.id || !p?.id || !p?.profile_type || isFetching.current) {
            return;
        }

        isFetching.current = true;
        setLoadingNegocios(true);
        setErrorNegocios(null);

        try {
            const dataPayload = { 
                user_id: user.id,
                ...filters,
            };
            // Only send company_id for empresa profiles
            if (p?.profile_type === 'empresa') {
                dataPayload.company_id = c?.id || p?.id;
            } else {
                // For main/professional profiles, send profile_id for filtering
                dataPayload.profile_id = p?.id;
            }

            const result = await negociosApi.listByCompany(dataPayload);

            if (result.status === "success" || result.success) {
                let negociosFinales = result.data || result.items || [];
                
                if (prospecto_id) {
                    negociosFinales = negociosFinales.filter(n => n.prospect_id == prospecto_id || n.prospectId == prospecto_id);
                }

                setNegocios(negociosFinales);
            } else {
                throw new Error(result.message || "Error al obtener los negocios");
            }

        } catch (error) {
            setErrorNegocios(error.message);
        } finally {
            setLoadingNegocios(false);
            isFetching.current = false;
        }
    }, [user?.id]);

    // 3. Función para forzar carga de un prospecto específico (Vista Detalle)
    const fetchNegociosPorProspecto = useCallback(async (prospecto_id) => {
        if (!prospecto_id) {

            return;
        }
        return await fetchNegocios(prospecto_id);
    }, [fetchNegocios]);

    /**
     * 4. Función para crear un nuevo negocio
     */
    const createNegocio = useCallback(async (negocioData) => {
        try {
            const payload = {
                profile_id: activeProfile?.id,
                profile_type: activeProfile?.profile_type,
                user_id: user.id,
                prospect_id: negocioData.prospect_id || null,
                campos: {
                    titulo: negocioData.name || negocioData.titulo || '',
                    valor: negocioData.value || negocioData.valor || 0,
                    stage_id: negocioData.stage_id ?? null,
                    probabilidad: negocioData.probability || negocioData.probabilidad || 10,
                    descripcion: negocioData.description || negocioData.descripcion || '',
                }
            };
            // Only send company_id for empresa profiles
            if (activeProfile?.profile_type === 'empresa') {
                payload.company_id = activeCompany?.id || activeProfile?.id;
            }
            
            const result = await negociosApi.create(payload);

            if (result.status === "success" || result.success) {
                // Recargar la lista
                await fetchNegocios();
                return result.data || result.item;
            } else {
                throw new Error(result.message || "Error al crear el negocio");
            }
        } catch (error) {
            throw error;
        }
    }, [user?.id, fetchNegocios, activeCompany?.id]);

    // 5. Recargar al cambiar de perfil
    useEffect(() => {
        const handleProfileChange = () => {
            if (user?.id) {
                fetchNegocios();
            }
        };
        window.addEventListener("crm:profileChanged", handleProfileChange);
        return () => window.removeEventListener("crm:profileChanged", handleProfileChange);
    }, [user?.id, fetchNegocios]);

    /**
     * 6. Función para actualizar un negocio
     */
    const updateNegocio = useCallback(async (negocioId, updateData) => {
        try {
            const payload = {
                id: negocioId,
                campos: updateData.campos || {
                    titulo: updateData.name || updateData.titulo,
                    valor: updateData.value || updateData.valor,
                    stage_id: updateData.stage_id ?? null,
                    probabilidad: updateData.probability || updateData.probabilidad,
                }
            };

            // Remove undefined fields
            Object.keys(payload.campos).forEach(key => {
                if (payload.campos[key] === undefined) {
                    delete payload.campos[key];
                }
            });

            const result = await negociosApi.update(payload);

            if (result.status === "success" || result.success) {
                // Actualizar localmente
                setNegocios(prev => prev.map(n => 
                    n.id === negocioId ? { ...n, ...updateData } : n
                ));
                return result.data;
            } else {
                throw new Error(result.message || "Error al actualizar el negocio");
            }
        } catch (error) {
            throw error;
        }
    }, []);

    /**
     * 7. Función para eliminar un negocio
     */
    const deleteNegocio = useCallback(async (negocioId) => {
        try {
            const { negociosApi } = await import("../services/apiClient");
            const result = await negociosApi.delete({ id: negocioId });

            if (result.status === "success" || result.success) {
                // Eliminar localmente
                setNegocios(prev => prev.filter(n => n.id !== negocioId));
                return true;
            } else {
                throw new Error(result.message || "Error al eliminar el negocio");
            }
        } catch (error) {
            throw error;
        }
    }, []);

    const value = {
        negocios,
        loadingNegocios,
        errorNegocios,
        fetchNegocios,
        fetchNegociosPorProspecto,
        createNegocio,
        updateNegocio,
        deleteNegocio,
    };

    return (
        <NegociosContext.Provider value={value}>
            {children}
        </NegociosContext.Provider>
    );
};

export const useNegocios = () => {
    const context = useContext(NegociosContext);
    if (!context) throw new Error("useNegocios debe estar dentro de NegociosProvider");
    return context;
};